import { createContext, useContext, useState, type ReactNode } from "react";
import {
  Snackbar,
  Alert,
  type AlertColor,
  Button,
  IconButton,
  Portal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * SnackbarContext
 *
 * Provides a context for showing snackbar notifications anywhere in the app.
 */

interface SnackbarContextType {
  showSnackbar: (
    message: string,
    severity?: AlertColor,
    actionButtonText?: string,
    onActionButtonClick?: () => void,
  ) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined,
);

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<AlertColor>("info");
  const [actionText, setActionText] = useState<string | undefined>(undefined);
  const [onActionClick, setOnActionClick] = useState<(() => void) | undefined>(
    undefined,
  );

  const showSnackbar = (
    msg: string,
    sev: AlertColor = "info",
    actionText?: string,
    onActionClick?: () => void,
  ) => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
    setActionText(actionText);
    setOnActionClick(onActionClick ? () => onActionClick : undefined);
  };

  const handleClose = () => setOpen(false);
  const handleActionClick = () => {
    onActionClick?.();
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Portal>
        <Snackbar
          open={open}
          autoHideDuration={10000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{
            "&.MuiSnackbar-anchorOriginBottomCenter": {
              bottom: {
                xs: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                sm: 24,
              },
            },
          }}
        >
          <Alert
            variant="filled"
            onClose={handleClose}
            severity={severity}
            sx={{ width: "100%" }}
            action={
              actionText !== undefined ? (
                <>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleActionClick}
                  >
                    {actionText}
                  </Button>
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    sx={{ p: 0.5 }}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              ) : null
            }
          >
            {message}
          </Alert>
        </Snackbar>
      </Portal>
    </SnackbarContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useSnackbar = () => {
  const context = useContext<SnackbarContextType | undefined>(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
